import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const SB_WIDTH = 260;
const SB_RAIL  = 56;

const NAV_ITEM = ({ icon, label, active, onClick, collapsed, indent = false }) => (
  <div onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 12,
      padding: collapsed ? '11px 0' : indent ? '9px 20px 9px 40px' : '11px 20px',
      cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start',
      color: active ? '#2196F3' : '#444',
      background: active ? '#E3F2FD' : 'transparent',
      borderRight: active ? '3px solid #2196F3' : '3px solid transparent',
      fontWeight: active ? 600 : 400, fontSize: indent ? 13 : 14,
      transition: 'all 0.15s', userSelect: 'none',
    }}
    onMouseOver={e => { if (!active) e.currentTarget.style.background = '#f5f5f5'; }}
    onMouseOut={e => { if (!active) e.currentTarget.style.background = active ? '#E3F2FD' : 'transparent'; }}>
    <span style={{ fontSize: indent ? 16 : 20, flexShrink: 0 }}>{icon}</span>
    {!collapsed && <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>}
  </div>
);

const GROUP_HEADER = ({ icon, label, open, onClick, onToggle, collapsed, active }) => (
  <div onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 12,
      padding: collapsed ? '11px 0' : '11px 20px',
      cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start',
      color: active ? '#2196F3' : '#444',
      background: active ? '#EFF6FF' : 'transparent',
      borderRight: active ? '3px solid #2196F3' : '3px solid transparent',
      fontWeight: 500, fontSize: 14, transition: 'all 0.15s', userSelect: 'none',
    }}
    onMouseOver={e => { if (!active) e.currentTarget.style.background = '#f5f5f5'; }}
    onMouseOut={e => { if (!active) e.currentTarget.style.background = active ? '#EFF6FF' : 'transparent'; }}>
    <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
    {!collapsed && <>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <span
        onClick={e => { e.stopPropagation(); onToggle && onToggle(); }}
        style={{ fontSize: 16, color: '#888', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', padding: '6px 8px', lineHeight: 1 }}>▼</span>
    </>}
  </div>
);

// Scroll-to-top button — globally visible on any page rendered inside the
// Sidebar layout. Watches the .sb-main scroll container; appears after 300px.
function ScrollToTopBtn() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const sc = document.querySelector('.sb-main') || window;
    const onScroll = () => {
      const pos = sc === window ? window.pageYOffset : sc.scrollTop;
      setShow(pos > 300);
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => sc.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => { const sc = document.querySelector('.sb-main') || window; sc.scrollTo({ top: 0, behavior: 'smooth' }); }}
      title="Back to top"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 200,
        width: 46, height: 46, borderRadius: '50%',
        background: 'linear-gradient(135deg, #2196F3, #1565C0)',
        color: '#fff', border: 'none', cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(33,150,243,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5"></line>
        <polyline points="5 12 12 5 19 12"></polyline>
      </svg>
    </button>
  );
}

export default function Sidebar({ user, onLogout, activeView, onViewChange, adminPanelRef, hasFeature, children }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('pm_sb_collapsed') === '1'; } catch { return false; }
  });
  // Groups default to OPEN (both desktop + mobile) so all sub-items are
  // visible without an extra click. Admin can fold each group via its chevron.
  const [menuOpen, setMenuOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(true);
  const [activeSub, setActiveSub] = useState(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Mobile UX: when admin opens the hamburger drawer, expand ALL groups
  // (Menu Manager, Admin Panel, TVs Manager) so every sub-item is visible at
  // once. Admin can still fold each group manually with its chevron.
  useEffect(() => {
    if (isMobile && open) {
      setMenuOpen(true);
      setAdminOpen(true);
      setPlayerOpen(true);
    }
  }, [isMobile, open]);

  // Submenu fly-out for collapsed sidebar — clicking (or right-clicking) a group icon
  // shows its submenu in a small panel beside the rail. We use position:fixed with the
  // trigger's actual viewport y-coordinate, because the inner <nav> has overflow:hidden
  // which would clip a position:absolute panel to the 56px-wide rail (= invisible).
  // Closes on outside click, Escape, or choosing an item.
  const [hoveredGroup, setHoveredGroup] = useState(null); // 'menu' | 'admin' | null
  const [flyoutTop, setFlyoutTop] = useState(0);
  const openGroup = (key, triggerEl) => {
    if (triggerEl) {
      const r = triggerEl.getBoundingClientRect();
      setFlyoutTop(r.top);
    }
    setHoveredGroup(g => g === key ? null : key);
  };
  useEffect(() => {
    if (!hoveredGroup) return;
    const onDocClick = e => {
      // Ignore clicks inside the fly-out itself OR inside any sidebar group trigger
      // (so toggling the same icon or switching to another group works without
      // the listener immediately closing the menu).
      if (e.target.closest && (
        e.target.closest('[data-sb-flyout]') ||
        e.target.closest('[data-sb-group-trigger]')
      )) return;
      setHoveredGroup(null);
    };
    const onKey = e => { if (e.key === 'Escape') setHoveredGroup(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [hoveredGroup]);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('pm_sb_collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Clear sub-item highlight when navigating away from menu manager
  useEffect(() => {
    if (activeView !== 'menuManager') setActiveSub(null);
  }, [activeView]);

  // Follow the menu app instead of remembering the last click. Without this the
  // rail lied whenever the user moved around inside the app — opening an info
  // screen template still highlighted Slides.
  useEffect(() => {
    const h = (e) => { const s = e?.detail?.sub; if (s) setActiveSub(s); };
    window.addEventListener('menuapp:sub', h);
    return () => window.removeEventListener('menuapp:sub', h);
  }, []);

  const goMenu = (path) => {
    window.__menuapp_initial_path = path;
    onViewChange('menuManager');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('menuapp:navigate', { detail: { path } }));
    }, 80);
    setOpen(false);
  };

  // Always route through BrandSelect so the stored brand is validated against the
  // current user's own brands — avoids "Failed to load menu" when pm_last_brand
  // points to a brand owned by a different user (e.g. stale admin session).
  const goMenuTarget = (target, subKey) => {
    goMenu(`/brand-select?target=${target}`);
    setActiveSub(subKey || target);
  };

  const goAdmin = (tab) => {
    onViewChange('adminPanel');
    setAdminOpen(true);
    setOpen(false);
    setTimeout(() => {
      if (adminPanelRef && adminPanelRef.current && adminPanelRef.current.switchTab) {
        adminPanelRef.current.switchTab(tab);
      }
    }, 60);
  };

  const go = (view) => { onViewChange(view); setOpen(false); };

  // On mobile the sidebar is a full-width drawer — never render in collapsed/rail
  // mode (which would hide all labels even though CSS forces width to 260px).
  const C = collapsed && !isMobile;

  const isAdmin  = user && user.is_admin;
  // Owners are the only non-admin role there is, now that staff
  // (brand_manager / branch_manager / operator) is gone — kept as a named
  // constant because several gates below read it.
  const userRole   = user?.role || (isAdmin ? 'admin' : 'owner');
  const ownerLevel = userRole === 'owner';
  // Use passed hasFeature function (mirrors App.js logic) or fall back to admin check
  const canGroup  = hasFeature ? hasFeature('groups')  : isAdmin;
  const canMenu   = hasFeature ? hasFeature('menu')    : isAdmin;   // DB stores 'menu'
  const canInfoScreen = hasFeature ? hasFeature('info_screen') : isAdmin;
  const canLiveStream = hasFeature ? hasFeature('live_stream') : isAdmin;

  // Pre-compute banner visibility so we can use it for sb-main padding on mobile.
  // Only show for active pro users within 10 days of expiry (bannerDays 1–10).
  // bannerDays <= 0 means already expired → backend will downgrade on next profile
  // fetch, so hide the banner and treat as regular user in the UI.
  const bannerDays = (user?.user_type === 'pro' && user?.pro_expires_at)
    ? Math.ceil((new Date(user.pro_expires_at) - new Date()) / 86400000) : null;
  const showBanner = bannerDays !== null && bannerDays > 0 && bannerDays <= 10;

  return (
    <div style={{ display: 'flex', height: '100vh', height: '100dvh', overflow: 'hidden',
      /* On mobile the sidebar is off-screen so content starts at left:0 */
      '--sb-width': isMobile ? '0px' : (C ? `${SB_RAIL}px` : `${SB_WIDTH}px`),
      '--sb-banner': showBanner ? '30px' : '0px',
      /* --sb-top: where fixed children (file manager) should start vertically */
      '--sb-top': showBanner ? (isMobile ? '82px' : '30px') : (isMobile ? '52px' : '0px'),
    }}>
      {/* Mobile overlay */}
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 299, display: 'none' }}
          className="sb-overlay" />
      )}

      {/* ===== SIDEBAR ===== */}
      <aside className={`sb-sidebar${open ? ' open' : ''}${C ? ' collapsed' : ''}`}>

        {/* Logo */}
        <div style={{
          padding: C ? '14px 8px' : '14px 16px', borderBottom: '1px solid #E0E0E0',
          display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
          minHeight: 64, boxSizing: 'border-box',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
            flex: 1, minWidth: 0, justifyContent: C ? 'center' : 'flex-start',
          }} onClick={() => go('home')}>
            <img src="/logo.png" alt="Promedia"
              style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', flexShrink: 0 }} />
            {!C && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#212121' }}>Promedia</div>
                <div style={{ fontSize: 11, color: '#757575' }}>Signage System</div>
              </div>
            )}
          </div>
          <button className="sb-toggle" onClick={() => setCollapsed(p => !p)}
            title={C ? 'Expand' : 'Collapse'}
            style={{
              flexShrink: 0, width: 28, height: 28, borderRadius: 7,
              border: '1.5px solid #E0E0E0', background: '#f9f9f9', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, color: '#666', transition: 'all 0.15s',
            }}>
            {C ? '›' : '‹'}
          </button>
          <button className="sb-close" onClick={() => setOpen(false)}
            style={{
              flexShrink: 0, background: '#f5f5f5', border: 'none', borderRadius: 8,
              width: 32, height: 32, fontSize: 17, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555',
            }}>✕</button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', padding: '6px 0' }}>

          {/* TVs Manager — group header. Click the header → opens Players
              (the TV grid, the app's home view). Chevron toggles the dropdown
              with: Players · Files · Groups. Info Screen lives in Display
              Manager below, not here. */}
          <div data-sb-group-trigger style={{ position: 'relative' }}
            onContextMenu={e => { if (C) { e.preventDefault(); openGroup('player', e.currentTarget); } }}>
            <GROUP_HEADER icon="📺" label="TVs Manager" collapsed={C}
              open={playerOpen}
              active={['home','fileManager','groupManager'].includes(activeView)}
              onClick={(e) => {
                if (C) { openGroup('player', e.currentTarget); return; }
                go('home'); setPlayerOpen(true);
              }}
              onToggle={() => setPlayerOpen(p => !p)} />
            {!C && playerOpen && (
              <div>
                <NAV_ITEM icon="🖥️" label="Players" indent collapsed={false}
                  active={activeView === 'home'}
                  onClick={() => go('home')} />
                <NAV_ITEM icon="📁" label="Files" indent collapsed={false}
                  active={activeView === 'fileManager'}
                  onClick={() => go('fileManager')} />
                {canGroup && (
                  <NAV_ITEM icon="🧩" label="Groups" indent collapsed={false}
                    active={activeView === 'groupManager'}
                    onClick={() => go('groupManager')} />
                )}
              </div>
            )}
            {/* Click fly-out (collapsed mode only) */}
            {C && hoveredGroup === 'player' && createPortal(
              <div data-sb-flyout
                onContextMenu={e => e.preventDefault()}
                style={{ position: 'fixed', left: SB_RAIL, top: flyoutTop, zIndex: 9999,
                  background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                  border: '1px solid #E0E0E0', borderRadius: '0 10px 10px 0',
                  minWidth: 200, padding: '6px 0' }}>
                <div style={{ padding: '8px 14px 6px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #F0F0F0' }}>TVs Manager</div>
                <NAV_ITEM icon="🖥️" label="Players" indent collapsed={false}
                  active={activeView === 'home'}
                  onClick={() => { go('home'); setHoveredGroup(null); }} />
                <NAV_ITEM icon="📁" label="Files" indent collapsed={false}
                  active={activeView === 'fileManager'}
                  onClick={() => { go('fileManager'); setHoveredGroup(null); }} />
                {canGroup && (
                  <NAV_ITEM icon="🧩" label="Groups" indent collapsed={false}
                    active={activeView === 'groupManager'}
                    onClick={() => { go('groupManager'); setHoveredGroup(null); }} />
                )}
              </div>,
              document.body
            )}
          </div>

          {/* Display Manager — 2026-10-27, renamed from "Screen Manager".
              Order per owner spec: F&B Menu, Info Screen, Live Stream (new),
              then Slides and AI Import unchanged after them.
              List Edit and QR Code are deliberately NOT here: both live in
              the Menu Manager page's own ☰, one click further in.
              Rendered for anyone holding ANY of the relevant capabilities —
              a user with only Live Stream still needs the group to show up
              with just that one child (no group-level "off" state; each
              child is independently gated). */}
          {(canMenu || canInfoScreen || canLiveStream) && (
            <div data-sb-group-trigger style={{ position: 'relative' }}
              onContextMenu={e => { if (C) { e.preventDefault(); openGroup('menu', e.currentTarget); } }}>
              <GROUP_HEADER icon="📁" label="Display Manager" collapsed={C}
                open={menuOpen} active={activeView === 'menuManager' && !activeSub}
                onClick={(e) => {
                  if (C) { openGroup('menu', e.currentTarget); return; }
                  // All non-admin (owners + staff) land on Menu Manager via
                  // BrandSelect — auto-redirects for single-brand users,
                  // shows the picker for multi-brand owners. Admins keep the
                  // Brands listing landing. Someone who only has info screens
                  // has nowhere else to land, so send them there.
                  if (isAdmin) {
                    goMenu('/'); setActiveSub('brands'); setMenuOpen(true);
                  } else {
                    goMenuTarget(canMenu ? 'menu' : 'info-screens');
                    setMenuOpen(true);
                  }
                }}
                onToggle={() => setMenuOpen(p => !p)} />
              {!C && menuOpen && (
                <div>
                  {/* Brands + Branches are no longer here for any non-admin
                      role — they now live as top-level entries (Branches for
                      everyone, Brands handled via Admin Panel / brand-edit
                      modals). Admins still reach them via the Admin Panel. */}
                  {canMenu && (
                    <NAV_ITEM icon="🍽️" label="F&B Menu" indent collapsed={false}
                      active={activeSub === 'menu'}
                      onClick={() => { goMenuTarget('menu'); }} />
                  )}
                  {canInfoScreen && (
                    <NAV_ITEM icon="🪧" label="Info Screen" indent collapsed={false}
                      active={activeSub === 'info-screens'}
                      onClick={() => { goMenuTarget('info-screens'); }} />
                  )}
                  {canLiveStream && (
                    <NAV_ITEM icon="🔴" label="Live Stream" indent collapsed={false}
                      active={activeView === 'liveStream'}
                      onClick={() => { go('liveStream'); }} />
                  )}
                  {canMenu && (
                    <NAV_ITEM icon="🎨" label="Slides"     indent collapsed={false}
                      active={activeSub === 'slides'}
                      onClick={() => { goMenuTarget('slides'); }} />
                  )}
                  {isAdmin && (
                    <NAV_ITEM icon="🤖" label="AI Import" indent collapsed={false}
                      active={activeSub === 'import'}
                      onClick={() => { goMenu('/import'); setActiveSub('import'); }} />
                  )}
                </div>
              )}
              {/* Click fly-out (collapsed mode only) — rendered via portal into document.body
                  to escape the transformed <aside> ancestor (which becomes the containing
                  block for any position:fixed descendant per CSS spec). */}
              {C && hoveredGroup === 'menu' && createPortal(
                <div data-sb-flyout
                  onContextMenu={e => e.preventDefault()}
                  style={{ position: 'fixed', left: SB_RAIL, top: flyoutTop, zIndex: 9999,
                    background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                    border: '1px solid #E0E0E0', borderRadius: '0 10px 10px 0',
                    minWidth: 200, padding: '6px 0' }}>
                  <div style={{ padding: '8px 14px 6px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #F0F0F0' }}>Display Manager</div>
                  {canMenu && (
                    <NAV_ITEM icon="🍽️" label="F&B Menu" indent collapsed={false}
                      active={activeSub === 'menu'}
                      onClick={() => { goMenuTarget('menu'); setHoveredGroup(null); }} />
                  )}
                  {canInfoScreen && (
                    <NAV_ITEM icon="🪧" label="Info Screen" indent collapsed={false}
                      active={activeSub === 'info-screens'}
                      onClick={() => { goMenuTarget('info-screens'); setHoveredGroup(null); }} />
                  )}
                  {canLiveStream && (
                    <NAV_ITEM icon="🔴" label="Live Stream" indent collapsed={false}
                      active={activeView === 'liveStream'}
                      onClick={() => { go('liveStream'); setHoveredGroup(null); }} />
                  )}
                  {canMenu && (
                    <NAV_ITEM icon="🎨" label="Slides"     indent collapsed={false}
                      active={activeSub === 'slides'}
                      onClick={() => { goMenuTarget('slides'); setHoveredGroup(null); }} />
                  )}
                  {isAdmin && (
                    <NAV_ITEM icon="🤖" label="AI Import" indent collapsed={false}
                      active={activeSub === 'import'}
                      onClick={() => { goMenu('/import'); setActiveSub('import'); setHoveredGroup(null); }} />
                  )}
                </div>,
                document.body
              )}
            </div>
          )}

          {/* Branches — top-level link for non-admin roles, but only when the
              account actually manages brand content (Menu Manager or Info
              Screens). A TV-only account (just Players/Files) has no brand
              content to scope into branches, so the entry is noise for them. */}
          {!isAdmin && (canMenu || canInfoScreen) && (
            <NAV_ITEM icon="🌿" label="Branches" collapsed={C}
              active={activeView === 'menuManager' && activeSub === 'branches'}
              onClick={() => { goMenuTarget('branches'); }} />
          )}

          {/* No customer-facing "User Manager" here. The whole staff /
              sub-user feature was removed 2026-10-27 (owner) — customers no
              longer invite managers or operators. The two "User Manager"
              items that remain below are ADMIN-only and point at the Admin
              Panel user list (goAdmin('users')), which is a different screen
              from the deleted menu-app /staff page. */}

          {/* Admin Panel (admin only) — header click opens User Control (default tab); arrow toggles submenu.
              Collapsed mode: click opens fly-out with all 3 admin destinations. */}
          {isAdmin && (
            <div data-sb-group-trigger style={{ position: 'relative' }}
              onContextMenu={e => { if (C) { e.preventDefault(); openGroup('admin', e.currentTarget); } }}>
              <GROUP_HEADER icon="⚙️" label="Admin Panel" collapsed={C}
                open={adminOpen} active={activeView === 'adminPanel'}
                onClick={(e) => {
                  if (C) { openGroup('admin', e.currentTarget); return; }
                  goAdmin('users'); setAdminOpen(true);
                }}
                onToggle={() => setAdminOpen(p => !p)} />
              {!C && adminOpen && (
                <div>
                  <NAV_ITEM icon="👥" label="User Manager" indent collapsed={false}
                    active={activeView === 'adminPanel'}
                    onClick={() => goAdmin('users')} />
                  <NAV_ITEM icon="🏷️" label="Brands"    indent collapsed={false}
                    active={false}
                    onClick={() => goMenu('/')} />
                  <NAV_ITEM icon="🌿" label="Branches"  indent collapsed={false}
                    active={false}
                    onClick={() => goMenuTarget('branches')} />
                  <NAV_ITEM icon="💾" label="Storage Monitor" indent collapsed={false}
                    active={false}
                    onClick={() => goAdmin('storage')} />
                  <NAV_ITEM icon="🗄️" label="Backup & Restore" indent collapsed={false}
                    active={false}
                    onClick={() => goAdmin('backup')} />
                </div>
              )}
              {/* Click fly-out (collapsed mode only) — portal escapes transformed <aside> */}
              {C && hoveredGroup === 'admin' && createPortal(
                <div data-sb-flyout
                  onContextMenu={e => e.preventDefault()}
                  style={{ position: 'fixed', left: SB_RAIL, top: flyoutTop, zIndex: 9999,
                    background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                    border: '1px solid #E0E0E0', borderRadius: '0 10px 10px 0',
                    minWidth: 200, padding: '6px 0' }}>
                  <div style={{ padding: '8px 14px 6px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #F0F0F0' }}>Admin Panel</div>
                  <NAV_ITEM icon="👥" label="User Manager" indent collapsed={false}
                    active={activeView === 'adminPanel'}
                    onClick={() => { goAdmin('users'); setHoveredGroup(null); }} />
                  <NAV_ITEM icon="🏷️" label="Brands"    indent collapsed={false}
                    active={false}
                    onClick={() => { goMenu('/'); setHoveredGroup(null); }} />
                  <NAV_ITEM icon="🌿" label="Branches"  indent collapsed={false}
                    active={false}
                    onClick={() => { goMenuTarget('branches'); setHoveredGroup(null); }} />
                  <NAV_ITEM icon="💾" label="Storage Monitor" indent collapsed={false}
                    active={false}
                    onClick={() => { goAdmin('storage'); setHoveredGroup(null); }} />
                  <NAV_ITEM icon="🗄️" label="Backup & Restore" indent collapsed={false}
                    active={false}
                    onClick={() => { goAdmin('backup'); setHoveredGroup(null); }} />
                </div>,
                document.body
              )}
            </div>
          )}

          {/* User Guide */}
          <NAV_ITEM icon="📖" label="User Guide" collapsed={C}
            active={false}
            onClick={() => window.open('https://promediadi.com/demo-studio/g/maestro-qxw8/s/xmu11vj8dngr', '_blank', 'noopener,noreferrer')} />

        </nav>

        {/* User footer */}
        {(() => {
          // 2026-05-18 (re-applied) — pro_features column dropped in Phase E.
          // Pro = any premium grant beyond the TV baseline, read from the
          // new `access` JSONB tree: tv.groups / tv.info_screen enabled, OR any
          // of the menu / pos / kitchen scopes enabled. user_type stays as a
          // legacy display tag.
          //
          // go_live is still read here on purpose. The feature was removed
          // 2026-09-07, but 158 existing users carry that grant and it is what
          // marks them as premium — dropping it would silently demote them.
          const a = user?.access || {};
          const tvPremium = !!(a.tv?.caps?.groups?.enabled
            || a.tv?.caps?.info_screen?.enabled
            || a.tv?.caps?.go_live?.enabled);
          const extraScope = !!(a.menu?.enabled || a.pos?.enabled || a.kitchen?.enabled);
          const isPro = tvPremium || extraScope || user?.user_type === 'pro';
          const daysLeft = (isPro && user?.pro_expires_at)
            ? Math.ceil((new Date(user.pro_expires_at) - new Date()) / 86400000) : null;
          // Only show days pill for active (not expired) pro accounts
          const showDays = daysLeft !== null && daysLeft > 0;
          const dayColor = (daysLeft !== null && daysLeft <= 10) ? '#DC2626' : '#D97706';
          const dayBg    = (daysLeft !== null && daysLeft <= 10) ? '#FEF2F2' : '#FFFBEB';
          const dayBorder= (daysLeft !== null && daysLeft <= 10) ? '#FCA5A5' : '#FDE68A';
          const dayLabel = daysLeft !== null && daysLeft <= 10
            ? `⚠️ ${daysLeft}d`
            : `${daysLeft}d left`;

          return (
            <div style={{ padding: C ? '10px 8px' : '12px 16px', borderTop: '1px solid #E0E0E0', flexShrink: 0 }}>
              {C ? (
                /* ── Collapsed: logout icon + days below ── */
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <button onClick={onLogout} title="Logout"
                    style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🚪</button>
                  {showDays && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: dayColor, background: dayBg, border: `1px solid ${dayBorder}`, borderRadius: 20, padding: '1px 5px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {dayLabel}
                    </span>
                  )}
                </div>
              ) : (
                /* ── Expanded: clean flex layout ── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {/* Row 1: avatar · username · logout */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    {/* Avatar */}
                    <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', background: user?.brand_logo ? '#f0f0f0' : '#2196F3', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                      {user?.brand_logo
                        ? <img src={user.brand_logo} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        : user?.username?.charAt(0).toUpperCase()}
                    </div>
                    {/* Username */}
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#212121' }}>
                      {user?.username}
                    </div>
                    {/* Logout */}
                    <button onClick={onLogout} title="Logout"
                      style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🚪</button>
                  </div>
                  {/* Row 2: role/tier badge + days pill — flush left.
                      Only owners show the legacy 'regular / pro' tier; admins
                      and staff (brand/branch manager, operator) show a clean
                      role badge with no tier text. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {(() => {
                      const role = user?.role || (user?.is_admin ? 'admin' : 'owner');
                      if (role === 'admin') {
                        return <span style={{ fontSize: 10, fontWeight: 700, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20, padding: '1px 8px' }}>Admin</span>;
                      }
                      if (role === 'brand_manager') {
                        return <span style={{ fontSize: 10, fontWeight: 700, color: '#3730A3', background: '#EEF2FF', border: '1px solid #A5B4FC', borderRadius: 20, padding: '1px 8px' }}>Brand Mgr</span>;
                      }
                      if (role === 'branch_manager') {
                        return <span style={{ fontSize: 10, fontWeight: 700, color: '#155E75', background: '#ECFEFF', border: '1px solid #67E8F9', borderRadius: 20, padding: '1px 8px' }}>Branch Mgr</span>;
                      }
                      if (role === 'operator') {
                        return <span style={{ fontSize: 10, fontWeight: 700, color: '#166534', background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 20, padding: '1px 8px' }}>Operator</span>;
                      }
                      // Owner — show pro/regular tier
                      return isPro
                        ? <span style={{ fontSize: 10, fontWeight: 700, color: '#C2410C', background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 20, padding: '1px 8px', letterSpacing: 0.4 }}>PRO</span>
                        : <span style={{ fontSize: 10, color: '#9CA3AF' }}>{user?.user_type || 'regular'}</span>;
                    })()}
                    {/* Pro-days countdown pill — owners only */}
                    {showDays && (user?.role || 'owner') === 'owner' && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: dayColor, background: dayBg, border: `1px solid ${dayBorder}`, borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                        {dayLabel}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </aside>

      {/* ── Pro expiry warning banner (≤10 days) ── */}
      {showBanner && (() => {
        const d = bannerDays;
        const expDate = new Date(user.pro_expires_at).toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' });
        const msg = d <= 0
          ? `⛔ Your Pro subscription has EXPIRED — please renew immediately to restore access!`
          : `⚠️ Your Pro subscription expires in ${d} day${d !== 1 ? 's' : ''} — on ${expDate}. Please renew before that date to avoid losing access! 🔔`;
        const bg  = d <= 3 ? '#DC2626' : '#D97706';
        // Desktop: flush with sidebar edge; Mobile: below the 52px topbar
        const bannerTop  = isMobile ? 52 : 0;
        const bannerLeft = isMobile ? 0 : (C ? `${SB_RAIL}px` : `${SB_WIDTH}px`);
        return (
          <div onClick={() => setMarqueePaused(p => !p)}
            title={marqueePaused ? 'Click to resume' : 'Click to pause'}
            style={{ position: 'fixed', top: bannerTop, left: bannerLeft, right: 0, zIndex: 249, background: bg, height: 30, overflow: 'hidden', display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ display: 'inline-block', whiteSpace: 'nowrap', animation: `sb-marquee 45s linear infinite`, animationPlayState: marqueePaused ? 'paused' : 'running', color: '#fff', fontSize: 12, fontWeight: 600 }}>
              {msg}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{msg}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{msg}
            </div>
            {marqueePaused && (
              <div style={{ position: 'absolute', right: 10, fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>▶ click to resume</div>
            )}
          </div>
        );
      })()}

      {/* ===== MAIN ===== */}
      {/* Push content below banner: desktop=30px, mobile=52px topbar+30px banner=82px */}
      <div className={`sb-main${C ? ' sb-main-collapsed' : ''}`}
           style={showBanner ? { paddingTop: isMobile ? 82 : 30 } : undefined}>
        {/* Mobile topbar */}
        <div className="sb-topbar">
          <button className="sb-hamburger" onClick={() => setOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 8,
              borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 5,
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
            <span style={{ display: 'block', width: 22, height: 2.5, background: '#212121', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2.5, background: '#212121', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2.5, background: '#212121', borderRadius: 2 }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
            onClick={() => go('home')}>
            <img src="/logo.png" alt="Promedia"
              style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'contain' }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Promedia Signage</span>
          </div>
        </div>

        {/* Page content */}
        {children}

        {/* Global scroll-to-top — shows on EVERY page (not just User Control)
            once the user scrolls past 300px in the .sb-main container. */}
        <ScrollToTopBtn />
      </div>

      <style>{`
        .sb-sidebar {
          width: ${SB_WIDTH}px;
          background: #fff;
          border-right: 1px solid #E0E0E0;
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0; left: 0;
          height: 100vh; height: 100dvh;
          z-index: 300;
          transition: width 0.22s ease, transform 0.28s cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
        }
        .sb-sidebar.collapsed { width: ${SB_RAIL}px; }
        .sb-main {
          flex: 1;
          min-width: 0;
          background: #f5f5f5;
          transition: margin-left 0.22s ease;
          margin-left: ${SB_WIDTH}px;
          overflow-y: auto;
          height: 100%;
        }
        .sb-main.sb-main-collapsed { margin-left: ${SB_RAIL}px; }

        @media (min-width: 769px) {
          .sb-sidebar { transform: translateX(0) !important; }
          .sb-close { display: none !important; }
          .sb-toggle { display: flex !important; }
          .sb-topbar { display: none !important; }
          .sb-overlay { display: none !important; }
        }
        @media (max-width: 768px) {
          .sb-sidebar { width: ${SB_WIDTH}px !important; transform: translateX(-100%); }
          .sb-sidebar.open { transform: translateX(0); box-shadow: 4px 0 24px rgba(0,0,0,0.18); }
          .sb-main { margin-left: 0 !important; }
          .sb-topbar {
            display: flex !important;
            align-items: center;
            gap: 14px;
            padding: 10px 16px;
            height: 52px;
            box-sizing: border-box;
            background: #fff;
            border-bottom: 1px solid #E0E0E0;
            /* Fixed (not sticky) on mobile — bullet-proof, no containing-block
               or flex-parent issues. Pair with padding-top on .sb-main below. */
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            z-index: 250;
          }
          /* Push content down by topbar height so it isn't hidden underneath. */
          .sb-main { padding-top: 52px; }
          .sb-toggle { display: none !important; }
          .sb-overlay { display: block !important; }
        }
        @keyframes sb-marquee {
          0%   { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
}
